/**
 * Payroll Export Page
 *
 * Simple page for downloading the payroll summary CSV export.
 * One row per crew member with their total pay for the period.
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { downloadReportCSV } from '../../../api/reports';
import { getErrorMessage } from '../../../api/client';
import { ReportFilters, useReportDateRange } from '../../../components/reports';

export default function PayrollExportPage() {
  const [dateRange, setDateRange] = useReportDateRange();
  const [isExporting, setIsExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleExport = async () => {
    setIsExporting(true);
    setError(null);

    try {
      await downloadReportCSV(
        '/reports/payroll-export',
        {
          startDate: new Date(dateRange.startDate).toISOString(),
          endDate: new Date(dateRange.endDate + 'T23:59:59').toISOString(),
        },
        `payroll-export-${dateRange.startDate}-to-${dateRange.endDate}.csv`
      );
      toast.success('Export downloaded successfully!');
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div>
      {/* Breadcrumb */}
      <nav className="mb-4">
        <Link to="/admin/reports" className="text-primary-600 hover:text-primary-700 text-sm">
          &larr; Back to Reports
        </Link>
      </nav>

      {/* Page header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Payroll Summary Export</h1>
        <p className="text-gray-600 mt-1">
          Download a CSV with one row per crew member showing their total hours and pay
        </p>
      </div>

      {/* Filters */}
      <ReportFilters
        dateRange={dateRange}
        onChange={setDateRange}
        onExportCSV={handleExport}
        isExporting={isExporting}
        exportLabel="Download CSV"
      />

      {/* Error message */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
          {error}
        </div>
      )}

      {/* Export info */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h2 className="text-lg font-medium text-gray-900 mb-4">Export Details</h2>
        <div className="space-y-3 text-sm text-gray-600">
          <p>
            <strong>Format:</strong> CSV (Comma-Separated Values)
          </p>
          <p>
            <strong>Content:</strong> One row per crew member with confirmed assignments in the date range
          </p>
          <p>
            <strong>Columns:</strong>
          </p>
          <ul className="list-disc list-inside ml-4 space-y-1">
            <li>Crew Name</li>
            <li>Email</li>
            <li>Total Hours</li>
            <li>Total Pay</li>
            <li>Assignment Count</li>
            <li>Event Count</li>
          </ul>
        </div>
      </div>
    </div>
  );
}
